import './FeedModel.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useRouter } from 'react-router5';
import { useDispatch, useSelector } from 'react-redux';

import { Block, Button } from '$uikit';
import { FeedModelProps } from './FeedModel.interface';
import { Attachments, UnitName, UnitPhoto, Verified, Viewer } from '$shared/components';
import { useDesktop, useTranslator, useUserInfo, useVisibleElement } from '$hooks';
import { CHAT, PROFILE, RESOLVE } from '$shared/constants/pages';
import { rnbr } from '$utils';
import { followersActions, followersSelector } from '$store/followers';
import { ViewerItem } from '$shared/components/Viewer/VieverItem/ViewerItem.interface';
import { mainActions } from '$store/main';
import { removeLinks } from '$shared/components/Linkify/Linkify';

const PhotoView: FC<{ viewerItems: ViewerItem[] }> = ({ viewerItems }) => {
  const dispatch = useDispatch();
  const isDesktop = useDesktop();
  const [currentViewerItem, setCurrentViewerItem] = useState(0);

  const handleCloseViewer = useCallback(() => {
    dispatch(mainActions.setPopup(null));
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCloseViewer();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentViewerItem]);

  return (
    <Viewer
      items={viewerItems}
      active={currentViewerItem}
      onChange={setCurrentViewerItem}
      onClose={handleCloseViewer}
      mobile={!isDesktop}
    />
  );
};

export const FeedModel: FC<FeedModelProps> = ({ object }) => {
  const { userId, videos, photos, content } = object;
  const user = useUserInfo(userId);
  const t = useTranslator();
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { following } = useSelector(followersSelector);
  const isFollowed = following[userId];
  const router = useRouter();

  const { visible } = useVisibleElement(ref, true);

  const routeParams = useMemo(() => {
    if (user.domain) {
      return {
        routeName: RESOLVE,
        routeParams: {
          domain: user.domain,
        },
      };
    } else {
      return {
        routeName: PROFILE,
        routeParams: {
          id: user.id,
        },
      };
    }
  }, [user]);

  const attachments = useMemo(() => {
    if (content.length > 0 && content[0].type === 'video') {
      return content.slice(0, 1);
    }

    return content.slice(0, 10);
  }, [content]);

  const handleClick = useCallback(
    (e) => {
      if (attachments.length > 0 && attachments[0].type === 'video') {
        return;
      }

      const target = e.target;
      if (target.closest('.Collage__item')) {
        dispatch(mainActions.setPopup(<PhotoView viewerItems={viewerItems} />));
        return;
      }
    },
    [attachments],
  );

  const viewerItems = useMemo(() => {
    if (attachments.length > 0 && attachments[0].type === 'video') {
      return [];
    }

    const items: ViewerItem[] = [];
    for (let i = 0; i < content.length; i++) {
      const item = content[i];
      items.push({
        id: item.photo!.photoId,
        source: item.photo!.photoLarge,
        width: item.photo!.width,
        height: item.photo!.height,
      });
    }

    return items;
  }, []);

  const stats = useMemo(() => {
    const items: string[] = [];
    if (photos > 0) {
      items.push(`${photos} photos`);
    }

    if (videos > 0) {
      items.push(`${videos} videos`);
    }

    return items.join(', ');
  }, [photos, videos]);

  const handleFollow = useCallback(() => {
    dispatch(followersActions.follow(userId));
  }, [userId]);

  const handleMessage = useCallback(() => {
    router.navigate(CHAT, { peerId: userId });
  }, [userId]);

  return (
    <Block className="FeedModel">
      <div className="FeedModel__header">
        <div className="FeedModel__photo">
          <Link {...routeParams}>
            <UnitPhoto unit={user} size="medium" />
          </Link>
        </div>
        <Link {...routeParams} className="FeedModel__header_cont">
          <div className="FeedModel__name_wrap">
            <div className="FeedModel__name">
              <UnitName unit={user} />
            </div>
            <Verified objectId={userId} />
          </div>
          <div className="FeedModel__stats">{stats}</div>
        </Link>
        <div className="FeedModel__stats__action">
          {isFollowed ? (
            <Button size="small" mode="tertiary" onClick={handleMessage}>
              {t('post_message')}
            </Button>
          ) : (
            <Button size="small" onClick={handleFollow}>
              {t('follow_button')}
            </Button>
          )}
        </div>
      </div>
      <div className="FeedModel__footer" ref={ref}>
        <div className="FeedModel__bio">{rnbr(removeLinks(user.bio))}</div>
        <div className="FeedModel__content" onClick={handleClick}>
          <Attachments
            parentObject={`model${userId}`}
            decryptKey=""
            attachments={attachments}
            isVisible={visible}
            isBlocked={false}
          />
        </div>
      </div>
    </Block>
  );
};
